// BoardSystem — the club's board. Sets a season objective when a league
// season starts, then judges the manager when it ends: confidence and
// manager reputation move up or down. Reaching zero confidence fires the
// manager (handled from Phase 6). Reads difficulty for leniency. Stateless
// w.r.t. the club (everything lives in the stores), so it survives club
// changes without re-init.

import { EventBus, GameEvents, type LeagueSeasonEndedPayload } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { useCareerStore } from '@/store/careerStore'
import {
  pickSeasonObjective,
  evaluateObjective,
  generateClubOffers,
  BOARD_CONFIG,
} from '@/config/careerConfig'
import { getDifficultyMods } from '@/config/difficultyConfig'
import { DIVISIONS } from '@/config/leagueConfig'
import { type Rng, defaultRng } from '../utils/rng'

export class BoardSystem {
  private onWeek = () => this.maybeSetObjective()
  private onSeasonEnded = (payload: LeagueSeasonEndedPayload) => this.judgeSeason(payload)

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.WEEK_CHANGED, this.onWeek)
    EventBus.on(GameEvents.LEAGUE_SEASON_ENDED, this.onSeasonEnded)
  }

  destroy() {
    EventBus.off(GameEvents.WEEK_CHANGED, this.onWeek)
    EventBus.off(GameEvents.LEAGUE_SEASON_ENDED, this.onSeasonEnded)
  }

  /** At the start of a league season (no objective yet), the board sets one */
  private maybeSetObjective() {
    const store = useGameStore.getState()
    const league = store.league
    if (store.gameResult || !league || store.seasonObjective) return

    // A new season starts: any standing offer the manager didn't take expires
    if (useCareerStore.getState().pendingOffers.length > 0) {
      useCareerStore.getState().clearOffers()
    }

    const lastPosition = store.leagueHistory.length > 0
      ? store.leagueHistory[store.leagueHistory.length - 1].position
      : null
    const objective = pickSeasonObjective(league.division, lastPosition, store.difficulty)
    store.setSeasonObjective(objective)

    EventBus.emit(GameEvents.BOARD_OBJECTIVE_SET, {
      objectiveLabel: objective.label,
      season: league.season,
      divisionName: DIVISIONS[league.division].name,
    })
  }

  /** When the season ends, judge the result against the objective */
  private judgeSeason(payload: LeagueSeasonEndedPayload) {
    const store = useGameStore.getState()
    const objective = store.seasonObjective
    if (!objective) return

    const success = evaluateObjective(objective, payload)
    const mods = getDifficultyMods()

    // Confidence: full reward on success; failure softened/hardened by leniency
    const delta = success
      ? objective.confidenceOnSuccess
      : objective.confidenceOnFailure / mods.boardLeniency
    store.setBoardConfidence(store.boardConfidence + delta)

    // Manager reputation (career-wide, separate from club reputation)
    useCareerStore.getState().addManagerReputation(
      success ? BOARD_CONFIG.reputationOnSuccess : BOARD_CONFIG.reputationOnFailure
    )

    EventBus.emit(
      success ? GameEvents.BOARD_OBJECTIVE_MET : GameEvents.BOARD_OBJECTIVE_FAILED,
      {
        objectiveLabel: objective.label,
        season: payload.season,
        divisionName: payload.divisionName,
        success,
        position: payload.position,
      }
    )
    EventBus.emit(GameEvents.BOARD_CONFIDENCE_CHANGED, {
      confidence: useGameStore.getState().boardConfidence,
    })

    // Objective consumed; the next season's objective is set on the next week
    store.setSeasonObjective(null)

    // Lost the dressing room: the board sacks the manager (alt game-over)
    if (useGameStore.getState().boardConfidence <= BOARD_CONFIG.fireThreshold) {
      this.fireManager(payload.season)
      return
    }

    // A strong season puts the manager on other clubs' radar
    if (success) this.maybeGenerateOffers()
  }

  private maybeGenerateOffers() {
    const career = useCareerStore.getState()
    if (career.pendingOffers.length > 0) return // don't pile up offers
    const offers = generateClubOffers(career.managerReputation, this.rng)
    if (offers.length === 0) return
    career.setPendingOffers(offers)
    EventBus.emit(GameEvents.CLUB_OFFER_RECEIVED, { offers })
  }

  private fireManager(season: number) {
    const store = useGameStore.getState()
    if (store.gameResult === 'lost') return // already over (e.g. bankruptcy)

    store.setGameResult('lost')
    store.setGameOverReason('fired')
    store.setPaused(true)

    EventBus.emit(GameEvents.GAME_OVER, { day: store.day, reason: 'fired' })
    EventBus.emit(GameEvents.MANAGER_FIRED, {
      clubName: store.managerClubName || 'tu club',
      season,
    })
  }
}
